// consent.js — the single gate in front of every non-essential tracker.
//
// Why a cookie and not localStorage: localStorage is per-origin, so a choice
// made on coinversa.ai would not be visible to developers.coinversa.ai and we
// would ask the same person twice. PostHog is already configured with
// `cross_subdomain_cookie: true` precisely so those two hosts resolve to one
// person — the consent that authorises that has to travel the same way.
//
// The contract this module owns: NOTHING that sets a non-essential cookie may
// initialise before `hasConsent()` returns true. Not "initialises but opts
// out" — PostHog's own opt-out flag is itself stored client-side, so the only
// state that provably writes nothing is "never called init". See src/index.js.

const COOKIE = 'cv_consent';

// Bump when the categories or the processors behind them change. An older
// version reads as "no decision yet" and the banner asks again, which is what
// a material change to processing requires.
const VERSION = 1;

const MAX_AGE_DAYS = 180;

export const CONSENT_EVENT = 'cv:consent-changed';

// Host-only cookie off production so a dev server cannot write a cookie that
// then leaks into the real apex domain (and vice versa).
function cookieDomain() {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  return /(^|\.)coinversa\.ai$/i.test(host) ? '; domain=.coinversa.ai' : '';
}

function readRaw() {
  if (typeof document === 'undefined') return null;
  try {
    const hit = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${COOKIE}=`));
    return hit ? decodeURIComponent(hit.slice(COOKIE.length + 1)) : null;
  } catch {
    // Cookies disabled entirely. Treat as "no decision", which keeps
    // trackers off — the safe direction to fail.
    return null;
  }
}

// Cookie format: `<version>:<state>[:<decidedAtMs>]`, shared with the portal
// (coinversa-developers src/lib/consent.js). Both sites now write
// `1:granted:<epoch ms>` / `1:denied:<epoch ms>`; the third segment is the
// browser's time of the decision, which the portal's /welcome sign-in sync
// forwards as `decidedAt` so the backend's latest-wins rule can order a
// decision made here against the stored record. The decision itself is read
// from the first two segments only, so `1:granted` (older writers) reads the
// same. The time lives inside the cookie so any rewrite replaces it
// atomically and a stale time is never paired with a newer decision.
const DECIDED_AT_SEGMENT = /^[1-9][0-9]{0,13}$/;

/**
 * Parses a raw cookie value into { state, decidedAt } (decidedAt: epoch ms, or
 * null when the cookie carries no usable time), or null when it holds no
 * decision for the current version. Same rules as the portal.
 */
export function parseConsentCookie(raw) {
  if (!raw) return null;
  const [v, value, at] = String(raw).split(':');
  if (String(v) !== String(VERSION)) return null;
  if (value !== 'granted' && value !== 'denied') return null;
  const ms = typeof at === 'string' && DECIDED_AT_SEGMENT.test(at) ? Number(at) : NaN;
  return { state: value, decidedAt: Number.isSafeInteger(ms) ? ms : null };
}

/** Parses a raw cookie value: 'granted' | 'denied' | null. */
export function parseConsent(raw) {
  const parsed = parseConsentCookie(raw);
  return parsed ? parsed.state : null;
}

/** 'granted' | 'denied' | null (no decision recorded for the current version). */
export function getConsent() {
  return parseConsent(readRaw());
}

/** Epoch ms of the current cookie decision, or null (none, or written without a time). */
export function getConsentDecidedAt() {
  const parsed = parseConsentCookie(readRaw());
  return parsed ? parsed.decidedAt : null;
}

export function hasConsent() {
  return getConsent() === 'granted';
}

/** True when we have never asked, or asked under an older consent version. */
export function needsDecision() {
  return getConsent() === null;
}

// Cross-tab / cross-subdomain sync (contract §3).
//
// `BroadcastChannel` reaches other tabs on the SAME origin immediately. The
// consent cookie is shared with developers.coinversa.ai, which no channel can
// reach, so every tab also re-reads the cookie whenever it becomes visible or
// focused. A message is only a hint: the cookie is always the source of truth.
const CHANNEL_NAME = 'cv_consent';
let channel = null;
let lastKnown; // last consent value this tab acted on (undefined = never read)

function getChannel() {
  if (channel) return channel;
  try {
    if (typeof BroadcastChannel === 'function') channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channel = null;
  }
  return channel;
}

function broadcast(state) {
  try {
    getChannel()?.postMessage({ state: state || 'unset' });
  } catch {
    /* no-op */
  }
}

/**
 * detail = { state: 'granted'|'denied'|null, surface?: 'banner'|'settings',
 *            origin: 'local'|'sync' }
 * `origin: 'sync'` means another tab or subdomain made the decision; the
 * decision's own tab already reported it, so sync must not emit again.
 */
function dispatch(detail) {
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail }));
  } catch {
    /* no-op */
  }
}

export function setConsent(value, { surface = 'banner' } = {}) {
  if (value !== 'granted' && value !== 'denied') {
    throw new Error(`setConsent expects 'granted' or 'denied', got ${value}`);
  }
  try {
    const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie =
      `${COOKIE}=${encodeURIComponent(`${VERSION}:${value}:${Date.now()}`)}` +
      `; path=/; max-age=${maxAge}; SameSite=Lax${cookieDomain()}` +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  } catch {
    // Nothing we can do; the in-page event below still lets this session
    // behave correctly, we just cannot remember the choice.
  }
  // Record what the cookie actually holds (not what we meant to write), so a
  // blocked cookie does not look like an external change on the next focus.
  lastKnown = getConsent();
  dispatch({ state: value, surface, origin: 'local' });
  broadcast(value);
}

/**
 * Withdrawal has to be as easy as granting, so this is what the "Cookie
 * settings" control calls. Clearing the decision re-shows the banner rather
 * than silently leaving trackers running.
 */
export function resetConsent() {
  try {
    document.cookie =
      `${COOKIE}=; path=/; max-age=0; SameSite=Lax${cookieDomain()}`;
  } catch {
    /* no-op */
  }
  lastKnown = getConsent();
  dispatch({ state: null, surface: 'settings', origin: 'local' });
  broadcast(null);
}

export function onConsentChange(handler) {
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}

/** Re-read the cookie; if it differs from what this tab last acted on, apply it. */
export function syncConsentFromCookie() {
  const now = getConsent();
  if (lastKnown === undefined) {
    lastKnown = now;
    return false;
  }
  if (now === lastKnown) return false;
  lastKnown = now;
  dispatch({ state: now, origin: 'sync' });
  return true;
}

/**
 * Start listening for decisions made in other tabs and on the other
 * subdomain. Idempotent; returns a stop function.
 */
let stopSync = null;
export function startConsentSync() {
  if (stopSync) return stopSync;
  if (typeof window === 'undefined') return () => {};
  if (lastKnown === undefined) lastKnown = getConsent();
  const onVisible = () => {
    try {
      if (document.visibilityState === 'visible') syncConsentFromCookie();
    } catch {
      /* no-op */
    }
  };
  const onFocus = () => syncConsentFromCookie();
  const onMessage = () => syncConsentFromCookie();
  const ch = getChannel();
  try {
    ch?.addEventListener('message', onMessage);
  } catch {
    /* no-op */
  }
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisible);
  stopSync = () => {
    try {
      ch?.removeEventListener('message', onMessage);
    } catch {
      /* no-op */
    }
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisible);
    stopSync = null;
  };
  return stopSync;
}

// Test hook: forget module state between cases.
export function __resetConsentModuleForTests() {
  if (stopSync) stopSync();
  try {
    channel?.close();
  } catch {
    /* no-op */
  }
  channel = null;
  lastKnown = undefined;
}
